import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Folder, ChevronDown, ChevronRight, FolderPlus, Plus, X, Inbox } from "lucide-react";
import { toast } from "sonner";

/**
 * FolderPicker - inline dropdown to pick a folder + create new ones.
 * value: folder_id | null (null = no folder)
 * onChange: (folder_id | null) => void
 */
export default function FolderPicker({ value, onChange, label = "Tallennuskansio (valinnainen)", inline = false }) {
  const [folders, setFolders] = useState([]);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [adding, setAdding] = useState(null); // parent_id or "ROOT"
  const [newName, setNewName] = useState("");
  const ref = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/folders");
      setFolders(data.folders || []);
    } catch (_e) {
      setFolders([]);
    }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const tree = useMemo(() => {
    const byParent = {};
    for (const f of folders) {
      const p = f.parent_id || "ROOT";
      if (!byParent[p]) byParent[p] = [];
      byParent[p].push(f);
    }
    Object.values(byParent).forEach((arr) => arr.sort((a, b) => a.name.localeCompare(b.name, "fi")));
    return byParent;
  }, [folders]);

  const byId = useMemo(() => Object.fromEntries(folders.map((f) => [f.folder_id, f])), [folders]);

  const breadcrumb = useMemo(() => {
    if (!value) return null;
    const path = [];
    let cur = byId[value];
    while (cur) {
      path.unshift(cur.name);
      cur = cur.parent_id ? byId[cur.parent_id] : null;
    }
    return path.join(" / ");
  }, [value, byId]);

  const submitNew = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      const { data } = await api.post("/folders", { name, parent_id: adding === "ROOT" ? null : adding });
      await load();
      setAdding(null); setNewName("");
      onChange?.(data.folder_id);
      toast.success("Kansio luotu");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Luonti epäonnistui");
    }
  };

  const renderNode = (folder, depth) => {
    const children = tree[folder.folder_id] || [];
    const isOpen = !!expanded[folder.folder_id];
    const selected = value === folder.folder_id;
    return (
      <div key={folder.folder_id}>
        <div
          className={`group flex items-center gap-1 rounded-lg ${selected ? "bg-emerald-50 text-emerald-900" : "hover:bg-slate-100"}`}
          style={{ paddingLeft: depth * 12 }}
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setExpanded((m) => ({ ...m, [folder.folder_id]: !isOpen })); }}
            className="h-7 w-5 flex items-center justify-center text-slate-400"
            aria-label="toggle"
          >
            {children.length > 0 ? (
              isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />
            ) : <span className="h-3.5 w-3.5" />}
          </button>
          <Folder className={`h-4 w-4 ${selected ? "text-emerald-600" : "text-slate-400"}`} />
          <button
            type="button"
            onClick={() => { onChange?.(folder.folder_id); setOpen(false); }}
            className="flex-1 text-left text-sm py-1.5 truncate"
            data-testid={`picker-folder-${folder.folder_id}`}
          >
            {folder.name}
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setAdding(folder.folder_id); setNewName(""); setExpanded((m) => ({ ...m, [folder.folder_id]: true })); }}
            className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-slate-200 text-slate-500"
            title="Uusi alikansio"
          >
            <Plus className="h-3 w-3" />
          </button>
        </div>
        {adding === folder.folder_id && (
          <div className="flex items-center gap-1 my-1" style={{ paddingLeft: (depth + 1) * 12 + 24 }}>
            <input
              autoFocus value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submitNew(); } if (e.key === "Escape") { setAdding(null); setNewName(""); } }}
              className="flex-1 px-2 py-1 text-sm rounded border border-emerald-300 outline-none"
              placeholder="Alikansion nimi"
            />
            <button onClick={submitNew} className="p-1.5 rounded bg-emerald-500 text-white"><Plus className="h-3 w-3" /></button>
            <button onClick={() => { setAdding(null); setNewName(""); }} className="p-1.5 rounded hover:bg-slate-100 text-slate-500"><X className="h-3 w-3" /></button>
          </div>
        )}
        {isOpen && children.map((c) => renderNode(c, depth + 1))}
      </div>
    );
  };

  const roots = tree.ROOT || [];

  // Inline mode: render the tree always expanded inside a scrollable box (used in dialogs)
  if (inline) {
    return (
      <div data-testid="folder-picker">
        {label && <label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>}
        <div className="border border-slate-200 rounded-xl bg-white max-h-[320px] overflow-y-auto p-2">
          <button
            type="button"
            onClick={() => onChange?.(null)}
            className={`w-full text-left rounded-lg px-2 py-1.5 text-sm flex items-center gap-2 ${value == null ? "bg-emerald-50 text-emerald-900 font-semibold" : "hover:bg-slate-100"}`}
            data-testid="picker-no-folder"
          >
            <Inbox className="h-4 w-4 text-slate-400" /> Ei kansiota (Hallinnoimaton)
          </button>
          <div className="mt-1 border-t border-slate-100 pt-1">
            {roots.length === 0 && adding !== "ROOT" && (
              <div className="text-xs text-slate-500 px-2 py-2">Ei kansioita vielä.</div>
            )}
            {roots.map((f) => renderNode(f, 0))}
            {adding === "ROOT" ? (
              <div className="flex items-center gap-1 mt-1 px-1">
                <input
                  autoFocus value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submitNew(); } if (e.key === "Escape") { setAdding(null); setNewName(""); } }}
                  className="flex-1 px-2 py-1 text-sm rounded border border-emerald-300 outline-none"
                  placeholder="Esim. As Oy Kirjokuja"
                />
                <button onClick={submitNew} className="p-1.5 rounded bg-emerald-500 text-white"><Plus className="h-3 w-3" /></button>
                <button onClick={() => { setAdding(null); setNewName(""); }} className="p-1.5 rounded hover:bg-slate-100 text-slate-500"><X className="h-3 w-3" /></button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => { setAdding("ROOT"); setNewName(""); }}
                className="w-full text-left rounded-lg px-2 py-1.5 mt-1 text-sm flex items-center gap-2 text-emerald-700 hover:bg-emerald-50"
                data-testid="picker-create-root"
              >
                <FolderPlus className="h-4 w-4" /> Uusi yläkansio
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={ref} className="relative" data-testid="folder-picker">
      {label && <label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="input-base text-left flex items-center gap-2"
        data-testid="folder-picker-toggle"
      >
        {value ? (
          <>
            <Folder className="h-4 w-4 text-emerald-600" />
            <span className="flex-1 truncate text-sm">{breadcrumb || "Valittu kansio"}</span>
          </>
        ) : (
          <>
            <Inbox className="h-4 w-4 text-slate-400" />
            <span className="flex-1 truncate text-sm text-slate-500">Ei kansiota (Hallinnoimaton)</span>
          </>
        )}
        <ChevronDown className="h-4 w-4 text-slate-400" />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-72 overflow-y-auto p-2">
          <button
            type="button"
            onClick={() => { onChange?.(null); setOpen(false); }}
            className={`w-full text-left rounded-lg px-2 py-1.5 text-sm flex items-center gap-2 ${value == null ? "bg-emerald-50 text-emerald-900 font-semibold" : "hover:bg-slate-100"}`}
            data-testid="picker-no-folder"
          >
            <Inbox className="h-4 w-4 text-slate-400" /> Ei kansiota
          </button>
          <div className="mt-1 border-t border-slate-100 pt-1">
            {roots.length === 0 && adding !== "ROOT" && (
              <div className="text-xs text-slate-500 px-2 py-2">Ei kansioita vielä.</div>
            )}
            {roots.map((f) => renderNode(f, 0))}
            {adding === "ROOT" ? (
              <div className="flex items-center gap-1 mt-1 px-1">
                <input
                  autoFocus value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submitNew(); } if (e.key === "Escape") { setAdding(null); setNewName(""); } }}
                  className="flex-1 px-2 py-1 text-sm rounded border border-emerald-300 outline-none"
                  placeholder="Esim. As Oy Kirjokuja"
                />
                <button onClick={submitNew} className="p-1.5 rounded bg-emerald-500 text-white"><Plus className="h-3 w-3" /></button>
                <button onClick={() => { setAdding(null); setNewName(""); }} className="p-1.5 rounded hover:bg-slate-100 text-slate-500"><X className="h-3 w-3" /></button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => { setAdding("ROOT"); setNewName(""); }}
                className="w-full text-left rounded-lg px-2 py-1.5 mt-1 text-sm flex items-center gap-2 text-emerald-700 hover:bg-emerald-50"
                data-testid="picker-create-root"
              >
                <FolderPlus className="h-4 w-4" /> Uusi yläkansio
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
